import React, { useState, useEffect, useRef } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { IoClose } from "react-icons/io5";
import { ChatMessage } from 'models/ai-chat/chat.types';
import { RootState } from 'redux/store';
import { useAppSelector } from 'redux/hooks';
import { FactDimTables } from 'models/ai-chat/data-explorer';
import useConversations from 'store/useConversationStore';
import LoadingIndicator from './loadingIndicator';
import { ChevronDown, ChevronRight, Table } from 'lucide-react';

interface DataExplorerProps {
  isOpen: boolean;
  onClose: () => void;
}

const DataExplorer: React.FC<DataExplorerProps> = ({ 
  isOpen, 
  onClose,
}) => {
  const [innerMessages, setInnerMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tables, setTables] = useState<FactDimTables[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [isInfoVisible, setIsInfoVisible] = useState<boolean>(false);
  const [reorderedTableData, setReorderedTableData] = useState<any[]>([]);
  const { factDimTables, tablePreviewData, loadingTable, loadingTablePreview } = useAppSelector((state: RootState) => state.conversations);
  const { loadTablePreview } = useConversations(false);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [innerMessages, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const onInformation = () => {
    setIsInfoVisible(!isInfoVisible)
  }

  useEffect(() => {
    if(factDimTables != null){
      setTables(factDimTables)
    }
  }, [factDimTables])

  useEffect(()=>{
    if(tablePreviewData!=null){
     // console.log(tablePreviewData)
      const originalData = tablePreviewData.data;
      setTableData(originalData);
      
      // Apply column reordering to move image columns first
      const reorderedData = reorderTableColumns(originalData);
      setReorderedTableData(reorderedData);
    }

  },[tablePreviewData])


  const handleTableClick = (tableName: string) => {
    setSelectedTable(selectedTable === tableName ? null : tableName);
    loadTablePreview(tableName)
  };

  // Helper functions for image detection and rendering
  const isImageColumn = (columnName: string) => {
    const column = columnName.toLowerCase();
    const imageKeywords = ['product_image', 'image', 'photo', 'picture', 'img', 'thumbnail'];
    return imageKeywords.some(keyword => column.includes(keyword));
  };

  const isImageUrl = (value: any) => {
    if (!value || typeof value !== 'string') return false;
    const url = value.toLowerCase().trim();
    return (
      url.startsWith('http://') || 
      url.startsWith('https://') || 
      url.startsWith('//')
    ) && (
      url.includes('.jpg') || 
      url.includes('.jpeg') || 
      url.includes('.png') || 
      url.includes('.gif') || 
      url.includes('.webp') || 
      url.includes('.svg')
    );
  };

  // Function to format column names - remove underscores and capitalize words
  const formatColumnName = (columnName: string) => {
    return columnName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const renderCellContent = (value: any, columnName: string) => {
    // Check if this is an image column and the value is an image URL
    if (isImageColumn(columnName) && isImageUrl(value)) {
      return (
        <div className="flex items-center justify-center">
          <img 
            src={String(value).trim()} 
            alt="Product" 
            className="w-12 h-12 object-cover rounded border"
            onError={(e) => {
              // Fallback to text if image fails to load
              const img = e.currentTarget as HTMLImageElement;
              const nextSibling = img.nextElementSibling as HTMLElement;
              if (img && nextSibling) {
                img.style.display = 'none';
                nextSibling.style.display = 'block';
              }
            }}
          />
          <span style={{ display: 'none' }} className="text-xs text-gray-500 truncate">
            {String(value)}
          </span>
        </div>
      );
    }
    
    // Default text rendering
    return (
      <span className="truncate max-w-xs block" title={String(value ?? '')}>
        {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}
      </span>
    );
  };

  // Function to reorder columns - move image column to first position
  const reorderTableColumns = (data: any[]) => {
    if (!data || data.length === 0) return data;
    
    const firstRow = data[0];
    const columnKeys = Object.keys(firstRow);
    
    // Find image column index
    const imageColumnIndex = columnKeys.findIndex(key => isImageColumn(key));
    
    // If no image column found or already first, return original data
    if (imageColumnIndex === -1 || imageColumnIndex === 0) return data;
    
    // Reorder columns: image column first, then rest
    const reorderedKeys = [
      columnKeys[imageColumnIndex], // Image column first
      ...columnKeys.filter((_, index) => index !== imageColumnIndex) // Rest of columns
    ];
    
    // Create new data with reordered columns
    return data.map(row => {
      const reorderedRow: any = {};
      reorderedKeys.forEach(key => {
        reorderedRow[key] = row[key];
      });
      return reorderedRow;
    });
  };

  if (!isOpen) return null;

  return (
    <><style>
        {`
          
          .invisible-scroll::-webkit-scrollbar-track {
            background: transparent;
            border: none;
          }
          
          .invisible-scroll::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 4px;
            border: none;
          }
          
          .invisible-scroll::-webkit-scrollbar-corner {
            background: background: rgba(255, 255, 255, 1.0);
          }
          
          .invisible-scroll {
            scrollbar-width: auto;
            scrollbar-color: rgba(0, 0, 0, 0.1) rgba(255, 255, 255, 1.0);
          }
        `}
      </style>
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="w-full md:w-[64rem] h-screen bg-white flex flex-col shadow-lg">
        <div className="px-8 py-4 border-b flex items-center justify-between bg-secondary-background text-primary-text flex-shrink-0">
          <div className="flex items-center text-primary-text flex-1 pr-2">
            <div className="text-ui truncate">
              Data Explorer
            </div>
          </div>
          {isInfoVisible && (
            <button 
              onClick={onInformation}
              className="text-primary-text transition-colors flex-shrink-0 mr-2"
              aria-label="Toggle info"
            >
              <FaInfoCircle />
            </button>
          )}
          <button 
            onClick={onClose}
            className="text-primary-text transition-colors flex-shrink-0"
            aria-label="Close thread"
          >
            <IoClose className='w-5 h-5'/>
          </button>
        </div>

        <div className="flex-1 overflow-auto invisible-scroll">
          {loadingTable ? (
            <div className="p-8 flex justify-center items-center">
               <LoadingIndicator /> 
            </div>
          ) : (
            <div className="p-4">
              {tables.length === 0 ? (
                <div className="text-center text-caption text-primary-text p-8">No tables found in the schema</div>
              ) : (
                <div className="grid gap-4 mb-4" 
                        style={{ 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                          gridAutoRows: 'minmax(50px, auto)'
                        }}>
                    {tables.map((tableName, index) => (
                      <button
                        key={index}
                        className={`px-4 py-2 border rounded-full shadow-sm text-left transition-all ${
                          selectedTable === tableName.table_name ? 'text-primary bg-blue-50 border-blue-300' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleTableClick(tableName.table_name)}
                      >
                        <div className="flex items-center h-full">
                          <Table className={`w-4 h-4 text-caption mr-2 flex-shrink-0 ${selectedTable === tableName.table_name ? 'text-primary' : 'text-primary-text'}`} />
                          <span className={`text-caption truncate ${selectedTable === tableName.table_name ? 'text-primary' : 'text-primary-text'}`}>{tableName.display_name}</span>
                          {selectedTable === tableName.table_name ? (
                            <ChevronDown className="w-5 h-5 ml-auto flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-5 h-5 ml-auto flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
              )}
              
              {selectedTable && (
                <div className="bg-white border rounded-[32px] overflow-hidden">
                  {
                    (loadingTablePreview) ?
                    <div className="p-8 flex justify-center items-center">
                        <LoadingIndicator /> 
                    </div> : (
                      Array.isArray(reorderedTableData) && reorderedTableData.length > 0 && (
                        <div className="px-8 py-4">
                          <div className="bg-gray-50 rounded overflow-x-auto invisible-scroll">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead>
                                <tr className="bg-white">
                                  {Object.keys(reorderedTableData[0]).map((key, idx) => (
                                    <th
                                      key={idx}
                                      className={`p-3 text-label text-primary-text uppercase tracking-wider whitespace-nowrap ${
                                        isImageColumn(key) ? 'text-center' : 'text-left'
                                      }`}
                                    >
                                      {formatColumnName(key)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {reorderedTableData.map((row: Record<string, any>, rowIdx: number) => (
                                  <tr
                                    key={rowIdx}
                                    className="bg-white border-t border-b border-tertiary-background"
                                  >
                                    {Object.keys(row).map((key, colIdx) => (
                                      <td
                                        key={colIdx}
                                        className={`p-3 text-ui text-primary-text ${
                                          isImageColumn(key) ? 'text-center' : 'truncate max-w-xs'
                                        }`}
                                      >
                                        {renderCellContent(row[key], key)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )
                    )
                  }
                  
  
                </div>
              )}
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>
    </div>
    </>
  );
};

export default DataExplorer;